import { useLocation, useNavigate, useParams } from "react-router-dom";
import React, { useState, useEffect, useCallback, useRef, JSX } from "react";
import api from "../hooks/api";
import { useAuth } from "../context/AuthContext";
import { API_URLS, getOrderComponentName, OrderComponets, vat } from "../App";
import { toDecimal, toPercentage } from "../helpers/PriceShopEngine";
import PriceInput from "../components/common/PriceInput";
import { useLayoutContext } from "../context/LayoutContext";
import CompoundPriceHistorySection from "../components/CompoundManageEditPage/CompoundPriceHistory";
import ActiveCampaignsSection from "../components/CompoundManageEditPage/ActiveCampaigns";
import Image from "../components/common/Image";
import {
  AlertCircle,
  Bot,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  CircleArrowLeft,
  CircuitBoard,
  Cpu,
  Fan,
  HardDrive,
  Keyboard,
  Lock,
  Loader2,
  MemoryStick,
  Microchip,
  Monitor,
  Mouse,
  Package,
  PcCase,
  Pencil,
  Power,
  RefreshCw,
  Rocket,
  ShieldOff,
  Sparkles,
  Trash2,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import {
  ManagementCompound,
  NewManagementCompound,
} from "../model/ManagementCompound";
import {
  ManagementCompoundComponent,
  NewManagementCompoundComponent,
} from "../model/ManagementCompoundComponent";
import { motion } from "framer-motion";
import ProductSearchInput from "../components/common/ProductSearchInput";
import * as compoundService from "../services/compoundService";
import type { CatalogCategory } from "../model/CatalogCategory";

// Define a type for your Product, if not already defined globally or in another file
interface Product {
  id: string;
  name: string;
  short_description: string;
  type: string;
  brand: string;
  cost_price: number;
  rev4_price: number;
  stock: number;
  is_base_component: boolean;
  reference: string;
}

const CompoundManageEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const { error, setError, success, setSuccess, loading, setLoading } =
    useLayoutContext();
  const [data, setData] = useState<ManagementCompound | null>(null);
  const [activeTab, setActiveTab] = useState<"components" | "history">(
    "components",
  );
  const compoundTabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [items, setItems] = useState<Product[]>([]); // This seems to be the list of components related to the compound
  const [file, setFile] = useState<File | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});
  const [editingComponentId, setEditingComponentId] = useState<string | null>(
    null,
  );
  const [editedComponentData, setEditedComponentData] =
    useState<ManagementCompoundComponent | null>(null);
  const [showAddComponentSection, setShowAddComponentSection] = useState(false);
  const [newComponentType, setNewComponentType] = useState<string>("");
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [statusOptions] = useState([
    { value: "DRAFT", label: "Rascunho" },
    { value: "PENDING", label: "Pendente" },
    { value: "PUBLISHED", label: "Publicado" },
    { value: "HIDDEN", label: "Oculto" },
    { value: "ARCHIVED", label: "Arquivado" },
  ]);
  const availableTags = [
    {
      value: "PROMOCAO",
      label: "Promoção",
      color: "bg-red-100 text-red-800 border-red-300",
    },
    {
      value: "NOVIDADE",
      label: "Novidade",
      color: "bg-blue-100 text-blue-800 border-blue-300",
    },
    {
      value: "DESTAQUE",
      label: "Destaque",
      color: "bg-yellow-100 text-yellow-800 border-yellow-300",
    },
    {
      value: "BEST_SELLER",
      label: "Best Seller",
      color: "bg-green-100 text-green-800 border-green-300",
    },
    {
      value: "OUTLET",
      label: "Outlet",
      color: "bg-purple-100 text-purple-800 border-purple-300",
    },
    {
      value: "IABUILDER",
      label: "IA Builder",
      color: "bg-cyan-100 text-cyan-800 border-cyan-300",
    },
  ];
  const [strategyPrice, setStrategyPrice] = useState<"erp" | "configured">(
    "configured",
  );
  const [aiDescLoading, setAiDescLoading] = useState(false);
  const [aiDescEditText, setAiDescEditText] = useState<string | null>(null);

  // Sync edit text when data.ai_description changes (e.g. after re-fetch)
  useEffect(() => {
    setAiDescEditText(null);
  }, [data?.ai_description]);

  const fetchCategories = useCallback(async () => {
    setError(null);
    api
      .get(API_URLS.CATALOG + "/metadata/categories")
      .then((res) => {
        const categories = (res.data as CatalogCategory[]).filter(
          (category) => category.is_filterable,
        );
        setCategories(categories);
      })
      .catch((err) => {
        setError("Ocorreu um erro ao buscar as categorias: " + err.message);
      })
      .finally(() => setLoading(null));
  }, [setLoading, setError]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: pathname } });
    }
    fetchData();
    fetchCategories();
  }, [id, isAuthenticated, navigate, pathname]); // Added dependencies

  const fetchItems = useCallback(async () => {
    try {
      const response = await api.get(
        API_URLS.COMPOUNDS + `/${encodeURIComponent(id!)}/items`,
      );
      setItems(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch items"); // Re-enabled error logging for items fetch
      console.warn("Error fetching items:", err.message);
    }
  }, [id, setError]); // Added dependencies

  const fetchData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      if (!id) {
        throw new Error("ID parameter is required");
      }
      const response = await api.get(
        API_URLS.COMPOUNDS + `/${encodeURIComponent(id)}`,
      );
      setData(NewManagementCompound(response.data));
      // Fetch items after fetching catalog data, but perhaps ensure `data` is set before `fetchItems` runs
      // Or pass `response.data.id` to fetchItems if it's immediately available and needed.
      // For now, keeping the await fetchItems() here assuming `id` is sufficient.
      await fetchItems();
    } catch (err: any) {
      setError(err.message || "Failed to fetch compound data");
      console.warn("Error fetching compound data:", err.message);
    } finally {
      setLoading(false);
    }
  }, [id, setError, setLoading, fetchItems]); // Added dependencies

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission behavior
    setError(null);
    setSuccess(null); // Clear previous success messages
    setLoading(true);
    try {
      if (!data) {
        throw new Error("No data to update.");
      }
      if (!id) {
        throw new Error("Compound ID is required.");
      }
      if (strategyPrice == "erp") {
        const confirmation = confirm(
          "Tem a certeza que pretende definir o preço de todos os componentes para o preço ERP?",
        );
        if (!confirmation) {
          setLoading(false);
          return;
        }
      }
      // ponytail: auto-save component from inline editor (new or persisted)
      let editorSaved = false;
      if (editedComponentData) {
        if (editingComponentId) {
          await compoundService.updateComponent(
            id!,
            editingComponentId,
            editedComponentData,
          );
        } else {
          await compoundService.addComponent(id!, editedComponentData);
        }
        editorSaved = true;
      }
      // ponytail: save remaining unsaved components from list fallback,
      // skip the editor component to avoid double-save
      const editedRef = editorSaved
        ? editedComponentData?.product_reference
        : undefined;
      const unsavedComponents =
        data.components_list?.filter(
          (c) => !c.id && c.product_reference !== editedRef,
        ) ?? [];
      const saveErrors: string[] = [];
      for (const comp of unsavedComponents) {
        try {
          await compoundService.addComponent(id!, comp);
        } catch (err: any) {
          saveErrors.push(
            `${comp.product_name || comp.component_type_key}: ${err.message}`,
          );
        }
      }
      if (saveErrors.length > 0) {
        setError(
          "Alguns componentes não foram guardados:\n" + saveErrors.join("\n"),
        );
        setLoading(false);
        return;
      }
      await compoundService.updateCompound(id!, {
        ...data,
        price_strategy: strategyPrice,
        components_list: undefined, // ponytail: pre-saved via POST above; strip to avoid stale data
      });
      await fetchData();
      setSuccess("Alterações guardadas com sucesso!");
    } catch (err: any) {
      setError(err.message || "Failed to update compound data");
      console.warn("Error updating compound data:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditComponent = (component: ManagementCompoundComponent) => {
    if (!editingComponentId && editedComponentData !== null) {
      // If we are already adding a component, cancel the edit first
      handleCancelEdit();
    }
    setEditingComponentId(component.id);
    setEditedComponentData(NewManagementCompoundComponent({ ...component })); // Create a mutable copy for editing
  };

  const handleCancelEdit = () => {
    if (!editingComponentId) {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          components_list:
            prev.components_list?.filter((c) => c.id !== editingComponentId) ??
            [],
        };
      });
    }
    setEditingComponentId(null);
    setEditedComponentData(null);
  };

  const handleSaveComponent = async () => {
    if (!editedComponentData || !id) return;

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (!editingComponentId) {
        await compoundService.addComponent(id!, editedComponentData);
        setSuccess("Componente adicionado com sucesso!");
      } else {
        await compoundService.updateComponent(
          id!,
          editingComponentId,
          editedComponentData,
        );
        setSuccess("Componente atualizado com sucesso!");
      }
      await fetchData();
      setEditingComponentId(null);
      setEditedComponentData(null);
    } catch (err: any) {
      setError(err.message || "Falha ao atualizar componente.");
      console.warn("Error updating component:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComponent = async (
    component: ManagementCompoundComponent,
  ) => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await compoundService.deleteComponent(id!, component.id);
      await fetchData();

      setSuccess("Componente removido com sucesso!");
    } catch (err: any) {
      setError(err.message || "Falha ao remover componente.");
      console.warn("Error deleting component:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComponentInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof ManagementCompoundComponent,
  ) => {
    if (editedComponentData) {
      setEditedComponentData(
        NewManagementCompoundComponent({
          ...editedComponentData,
          [field]:
            field === "base_component" && e.target instanceof HTMLInputElement
              ? e.target.checked
              : e.target.value,
          ...(field === "base_component" && e.target instanceof HTMLInputElement
            ? { optional: !e.target.checked }
            : {}),
        }),
      );
    }
  };

  const handleAddComponent = (componentTypeKey: string) => {
    // Lógica para adicionar um novo componente do tipo selecionado
    // Exemplo: abrir modal, ou adicionar linha de edição na tabela
    setShowAddComponentSection(false);
    if (!componentTypeKey) {
      setError("Por favor, selecione um tipo de componente.");
      return;
    }

    const newComponent: ManagementCompoundComponent = {
      id: "", // This will be set by the backend
      // @ts-ignore
      component_type_key: componentTypeKey,
      component_name: getOrderComponentName(componentTypeKey),
      product_name: "",
      product_reference: "",
      product_id: "",
      base_component: false,
      optional: true,
      price_pvp_with_vat: 0,
      price_pvp_without_vat: 0,
      vat_rate: vat, // Default VAT rate
      available_units: 0,
    };
    setData((prevData) => {
      if (!prevData) return prevData; // Ensure prevData is not null
      const updatedComponents = [
        ...(prevData.components_list ?? []),
        newComponent,
      ];
      return {
        ...prevData,
        components_list: updatedComponents,
      };
    });
    handleEditComponent(newComponent); // Start editing the new component immediately
    // focus on div with id: section-{componentTypeKey}
    setTimeout(() => {
      const sectionElement = document.getElementById(
        `section-${componentTypeKey}`,
      );
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 250);
  };

  // Function to toggle collapse state for a given type
  const toggleCollapse = (type: string) => {
    setCollapsedSections((prevState) => ({
      ...prevState,
      [type]: !prevState[type],
    }));
  };

  const icons: { [key: string]: JSX.Element } = {
    // Explicitly typed icons object
    Cpu: <Cpu className="inline mr-2" />, // Adjusted margin for better inline alignment
    Gpu: <Microchip className="inline mr-2" />,
    CircuitBoard: <CircuitBoard className="inline mr-2" />,
    MemoryStick: <MemoryStick className="inline mr-2" />,
    HardDrive: <HardDrive className="inline mr-2" />,
    Fan: <Fan className="inline mr-2" />,
    PcCase: <PcCase className="inline mr-2" />,
    Wifi: <Wifi className="inline mr-2" />,
    Monitor: <Monitor className="inline mr-2" />,
    Keyboard: <Keyboard className="inline mr-2" />,
    Mouse: <Mouse className="inline mr-2" />,
    Power: <Power className="inline mr-2" />,
    Rocket: <Rocket className="inline mr-2" />,
    Other: <Package className="inline mr-2" />,
  };

  if (!isAuthenticated()) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-xl text-gray-700">
          Você deve estar logado para editar itens.
        </p>
      </div>
    );
  }

  const isLoadingActive =
    typeof loading === "object" ? !!loading?.message : loading;

  if (loading && !data) {
    // Show initial loading state
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-xl text-gray-700">
          Carregando dados do computador...
        </p>
      </div>
    );
  }

  if (error && !data) {
    // Show initial error state
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-xl text-red-600">Erro: {error}</p>
      </div>
    );
  }

  // Ensure data exists before rendering the main form
  if (!data) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <p className="text-xl text-gray-700">
          Nenhum dado do computador encontrado.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 bg-gray-50 min-h-screen font-sans">
      {/* ── PAGE HEADER ── */}
      <header className="flex items-center gap-4 mb-8">
        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => history.back()}
          className="not-printable inline-flex items-center gap-2 px-3 py-2 rounded-lg
                     text-red-600 hover:text-red-700 hover:bg-red-50
                     focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2
                     transition-colors duration-150 text-sm font-medium flex-shrink-0"
          aria-label="Voltar à página anterior"
        >
          <CircleArrowLeft className="w-5 h-5" aria-hidden="true" />
          <span>Voltar</span>
        </motion.button>
        <h1 className="flex-1 text-3xl font-extrabold text-gray-900 leading-tight">
          Editar Computador
        </h1>
      </header>

      {/* ── FEEDBACK BANNERS ── */}
      {(success || error) && (
        <div aria-live="polite" aria-atomic="true" className="mb-6 space-y-3">
          {success && (
            <motion.div
              role="alert"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 px-5 py-4 rounded-xl shadow-sm"
            >
              <CheckCircle
                className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">
                  Operação concluída com sucesso
                </p>
                <p className="text-sm mt-0.5 text-emerald-700">{success}</p>
              </div>
              <button
                type="button"
                onClick={() => setSuccess(null)}
                className="flex-shrink-0 p-1 rounded-md text-emerald-500 hover:text-emerald-700 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-colors"
                aria-label="Fechar mensagem de sucesso"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </motion.div>
          )}
          {error && (
            <motion.div
              role="alert"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 px-5 py-4 rounded-xl shadow-sm"
            >
              <AlertCircle
                className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">Ocorreu um erro</p>
                <p className="text-sm mt-0.5 text-red-700">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="flex-shrink-0 p-1 rounded-md text-red-500 hover:text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
                aria-label="Fechar mensagem de erro"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </motion.div>
          )}
        </div>
      )}

      <div
        role="tablist"
        aria-label="Secções do composto"
        className="flex gap-1 border-b mb-4"
        onKeyDown={(e) => {
          if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
          const order: Array<"components" | "history"> =
            isAdmin && id ? ["components", "history"] : ["components"];
          const idx = order.indexOf(activeTab);
          const next =
            e.key === "ArrowRight"
              ? (idx + 1) % order.length
              : (idx - 1 + order.length) % order.length;
          e.preventDefault();
          setActiveTab(order[next]);
          compoundTabRefs.current[order[next]]?.focus();
        }}
      >
        <button
          type="button"
          role="tab"
          id="tab-compound-components"
          aria-controls="tabpanel-compound-components"
          ref={(el) => {
            compoundTabRefs.current["components"] = el;
          }}
          aria-selected={activeTab === "components"}
          tabIndex={activeTab === "components" ? 0 : -1}
          onClick={() => setActiveTab("components")}
          className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
            activeTab === "components"
              ? "border-blue-600 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Componentes
        </button>
        {isAdmin && id && (
          <button
            type="button"
            role="tab"
            id="tab-compound-history"
            aria-controls="tabpanel-compound-history"
            ref={(el) => {
              compoundTabRefs.current["history"] = el;
            }}
            aria-selected={activeTab === "history"}
            tabIndex={activeTab === "history" ? 0 : -1}
            onClick={() => setActiveTab("history")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === "history"
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Histórico de preços
          </button>
        )}
      </div>

      <form
        onSubmit={handleUpdate}
        role="tabpanel"
        id="tabpanel-compound-components"
        aria-labelledby="tab-compound-components"
        hidden={activeTab !== "components"}
      >
        <div className="bg-white shadow-lg rounded-xl p-8 mb-10">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Image Upload Section (Left Column) */}
            <div className="w-full md:w-1/3 flex flex-col items-center">
              <div className="w-full max-w-sm mb-6 bg-gray-100 rounded-lg overflow-hidden shadow-inner">
                {/* Current image or placeholder */}
                {file ? (
                  <picture>
                    <img
                      src={URL.createObjectURL(file)}
                      alt="Selected Computer Image"
                      className="w-full h-auto object-cover rounded-lg aspect-square"
                    />
                  </picture>
                ) : (
                  <Image
                    src={`https://placehold.co/600x600/E5E7EB/4B5563?text=Nenhuma Imagem`}
                    loading="progressive"
                    dataSrc={data.image}
                    alt={data.name ?? data.id}
                    className="w-full h-auto object-cover rounded-lg aspect-square"
                    sizes="600"
                  />
                )}
              </div>

              <div className="w-full flex justify-center">
                {file ? (
                  <div className="flex space-x-4 w-full justify-center">
                    <button
                      type="button"
                      className="px-3 py-3 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition duration-200 text-sm"
                      onClick={() => setFile(null)}
                    >
                      Remover Ficheiro
                    </button>
                    <button
                      type="button"
                      className="px-3 py-3 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition duration-200 text-sm"
                      onClick={() => {
                        if (file) {
                          const formData = new FormData();
                          formData.append("image", file);
                          setLoading(true);
                          api
                            .put(
                              API_URLS.COMPOUNDS +
                                `/${encodeURIComponent(id!)}/upload`,
                              formData,
                            )
                            .then(() => {
                              setSuccess("Ficheiro guardado com sucesso.");
                              fetchData(); // Re-fetch data to update image URL if backend returns new URL
                              setFile(null);
                            })
                            .catch((err: any) => {
                              setError(err.message || "Failed to upload file");
                            })
                            .finally(() => {
                              setLoading(false);
                            });
                        }
                      }}
                    >
                      Guardar Ficheiro
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer bg-blue-600 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition duration-200 text-sm">
                    <input
                      className="hidden" // Hide the default file input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={(e) => {
                        const selectedFile = e.target.files?.[0];
                        if (selectedFile) {
                          setFile(selectedFile);
                        }
                      }}
                    />
                    Escolher Ficheiro...
                  </label>
                )}
              </div>
            </div>

            {/* Product Details Section (Right Column) */}
            <div className="w-full md:w-2/3 space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nome
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    placeholder="Nome do Computador"
                  />
                </div>
                <div>
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Descrição
                  </label>
                  <input
                    id="description"
                    type="text"
                    className="mt-1 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={data.description}
                    onChange={(e) =>
                      setData({ ...data, description: e.target.value })
                    }
                    placeholder="Descrição do Computador"
                  />
                </div>

                {/* AI Description */}
                <div
                  className={`rounded-xl border shadow-sm overflow-hidden transition-colors ${
                    data.ai_description_status === "APPROVED"
                      ? "border-green-300 bg-green-50"
                      : "border-indigo-200 bg-indigo-50"
                  }`}
                >
                  {/* ── Card Header ── */}
                  <div
                    className={`flex items-center gap-2.5 px-4 py-3 border-b ${
                      data.ai_description_status === "APPROVED"
                        ? "bg-green-100/60 border-green-200"
                        : "bg-indigo-100/60 border-indigo-200"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-7 h-7 rounded-lg ${
                        data.ai_description_status === "APPROVED"
                          ? "bg-green-200 text-green-700"
                          : "bg-indigo-200 text-indigo-700"
                      }`}
                    >
                      <Bot className="w-4 h-4" />
                    </div>
                    <h3
                      className={`text-sm font-semibold ${
                        data.ai_description_status === "APPROVED"
                          ? "text-green-900"
                          : "text-indigo-900"
                      }`}
                    >
                      Descrição Gerada por IA
                    </h3>

                    {/* Status badge */}
                    {data.ai_description_status && (
                      <span
                        className={`ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                          data.ai_description_status === "APPROVED"
                            ? "bg-green-100 text-green-800 border-green-300"
                            : data.ai_description_status === "GENERATED"
                              ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                              : data.ai_description_status === "REJECTED"
                                ? "bg-red-100 text-red-800 border-red-300"
                                : "bg-gray-100 text-gray-500 border-gray-300"
                        }`}
                      >
                        {data.ai_description_status === "APPROVED" && (
                          <CheckCircle className="w-3.5 h-3.5" />
                        )}
                        {data.ai_description_status === "GENERATED" && (
                          <AlertCircle className="w-3.5 h-3.5" />
                        )}
                        {data.ai_description_status === "REJECTED" && (
                          <XCircle className="w-3.5 h-3.5" />
                        )}
                        {data.ai_description_status === "PENDING" && (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        {data.ai_description_status === "APPROVED" &&
                          "Aprovada — publicada no site"}
                        {data.ai_description_status === "GENERATED" &&
                          "Aguarda validação"}
                        {data.ai_description_status === "REJECTED" &&
                          "Rejeitada"}
                        {data.ai_description_status === "PENDING" && "Pendente"}
                      </span>
                    )}
                  </div>

                  {/* ── Card Body ── */}
                  <div className="px-4 py-3">
                    {aiDescLoading ? (
                      /* Loading skeleton */
                      <div className="flex flex-col items-center justify-center gap-2 py-6 text-indigo-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="text-xs font-medium">
                          A processar…
                        </span>
                      </div>
                    ) : data.ai_description ? (
                      /* Text area — read-only when APPROVED */
                      <div className="relative">
                        <textarea
                          className={`w-full text-sm rounded-lg px-3 py-2 resize-none transition-colors ${
                            data.ai_description_status === "APPROVED"
                              ? "bg-green-50 border border-green-200 text-green-900 cursor-not-allowed select-none"
                              : "bg-white border border-indigo-200 text-gray-700 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
                          }`}
                          rows={5}
                          readOnly={data.ai_description_status === "APPROVED"}
                          value={
                            aiDescEditText !== null
                              ? aiDescEditText
                              : data.ai_description
                          }
                          onChange={(e) => setAiDescEditText(e.target.value)}
                        />
                        {data.ai_description_status === "APPROVED" && (
                          <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-100 text-green-600 text-xs font-medium px-2 py-0.5 rounded-full border border-green-200 pointer-events-none">
                            <Lock className="w-3 h-3" />
                            Bloqueado
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Empty state */
                      <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-400">
                          <Sparkles className="w-5 h-5" />
                        </div>
                        <p className="text-sm font-medium text-indigo-700">
                          Nenhuma descrição gerada ainda
                        </p>
                        <p className="text-xs text-indigo-400 max-w-xs">
                          Clique em{" "}
                          <span className="font-semibold">Gerar descrição</span>{" "}
                          para criar automaticamente um texto optimizado para o
                          produto.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ── Card Footer — Action bar ── */}
                  <div
                    className={`flex flex-wrap items-center gap-2 px-4 py-3 border-t ${
                      data.ai_description_status === "APPROVED"
                        ? "bg-green-100/40 border-green-200"
                        : "bg-indigo-100/40 border-indigo-200"
                    }`}
                  >
                    {/* Primary: Generate / Regenerate */}
                    <button
                      type="button"
                      disabled={aiDescLoading}
                      onClick={async () => {
                        if (data.ai_description_status === "APPROVED") {
                          const confirmed = window.confirm(
                            "Esta descrição já foi aprovada. Tem a certeza que quer regenerar e perder a aprovação?",
                          );
                          if (!confirmed) return;
                        }
                        setAiDescLoading(true);
                        try {
                          const res = await api.post(
                            API_URLS.COMPOUNDS +
                              `/${encodeURIComponent(id!)}/ai-description/generate`,
                          );
                          setData(NewManagementCompound(res.data));
                          setAiDescEditText(null);
                        } catch (err: any) {
                          setError(err.message || "Erro ao gerar descrição IA");
                        } finally {
                          setAiDescLoading(false);
                        }
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                    >
                      {data.ai_description ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5" />
                          Regenerar
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3.5 h-3.5" />
                          Gerar descrição
                        </>
                      )}
                    </button>

                    {/* Secondary: Approve — only when GENERATED */}
                    {data.ai_description_status === "GENERATED" && (
                      <button
                        type="button"
                        disabled={aiDescLoading}
                        onClick={async () => {
                          setAiDescLoading(true);
                          try {
                            const res = await api.patch(
                              API_URLS.COMPOUNDS +
                                `/${encodeURIComponent(id!)}/ai-description`,
                              {
                                action: "approve",
                                text: aiDescEditText ?? undefined,
                              },
                            );
                            setData(NewManagementCompound(res.data));
                            setAiDescEditText(null);
                            setSuccess("Descrição IA aprovada!");
                          } catch (err: any) {
                            setError(
                              err.message || "Erro ao aprovar descrição IA",
                            );
                          } finally {
                            setAiDescLoading(false);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Aprovar
                      </button>
                    )}

                    {/* Destructive group — pushed to the right */}
                    <div className="ml-auto flex items-center gap-2">
                      {/* Reject — only when GENERATED */}
                      {data.ai_description_status === "GENERATED" && (
                        <button
                          type="button"
                          disabled={aiDescLoading}
                          onClick={async () => {
                            setAiDescLoading(true);
                            try {
                              const res = await api.patch(
                                API_URLS.COMPOUNDS +
                                  `/${encodeURIComponent(id!)}/ai-description`,
                                { action: "reject" },
                              );
                              setData(NewManagementCompound(res.data));
                              setAiDescEditText(null);
                            } catch (err: any) {
                              setError(
                                err.message || "Erro ao rejeitar descrição IA",
                              );
                            } finally {
                              setAiDescLoading(false);
                            }
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-white text-red-600 border border-red-300 rounded-lg hover:bg-red-50 active:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Rejeitar
                        </button>
                      )}

                      {/* Revoke — only when APPROVED */}
                      {data.ai_description_status === "APPROVED" && (
                        <button
                          type="button"
                          disabled={aiDescLoading}
                          onClick={async () => {
                            setAiDescLoading(true);
                            try {
                              const res = await api.patch(
                                API_URLS.COMPOUNDS +
                                  `/${encodeURIComponent(id!)}/ai-description`,
                                { action: "revoke" },
                              );
                              setData(NewManagementCompound(res.data));
                              setAiDescEditText(null);
                              setSuccess(
                                "Aprovação revogada — descrição retorna a GENERATED.",
                              );
                            } catch (err: any) {
                              setError(
                                err.message || "Erro ao revogar aprovação",
                              );
                            } finally {
                              setAiDescLoading(false);
                            }
                          }}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-white text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50 active:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ShieldOff className="w-3.5 h-3.5" />
                          Revogar aprovação
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing Information */}
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-800 mb-5">
                    Informações de Preço
                  </h2>

                  {/* H5 — fieldset/legend for radio group */}
                  <fieldset className="mb-5">
                    <legend className="text-sm font-medium text-gray-700 mb-2">
                      Estratégia de Preço
                    </legend>
                    <div className="flex gap-6">
                      {(["configured", "erp"] as const).map((val) => (
                        <label
                          key={val}
                          className="inline-flex items-center gap-2 cursor-pointer group"
                        >
                          <input
                            type="radio"
                            name="strategyPrice"
                            value={val}
                            checked={strategyPrice === val}
                            onChange={() => setStrategyPrice(val)}
                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                            {val === "configured"
                              ? "Preço Configurado"
                              : "Preço ERP"}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {/* H6 — read-only price metrics with dt/dd (not <label>) */}
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                    {[
                      {
                        label: "Preço Custo Niposom",
                        value: `${toDecimal(data.getBaseComponentsCostPrice(strategyPrice))} EUR`,
                      },
                      {
                        label: "Preço REV4",
                        value: `${toDecimal(data.getBaseComponentsRev4Price(strategyPrice))} EUR`,
                      },
                      {
                        label: "Preço Revendedor",
                        value: `${toDecimal(data.getBaseComponentsResellerPrice(strategyPrice))} EUR`,
                      },
                      {
                        label: "Desconto Revendedor (margem)",
                        value: `${toPercentage(data.getBaseDiscountRate(strategyPrice))} %`,
                      },
                      {
                        label: "Margem Revendedor",
                        value: `${toDecimal(data.getResellerGrossGainValue(strategyPrice))} EUR`,
                      },
                      {
                        label: "Percentagem Margem",
                        value: `${toPercentage(data.getResellerRecommendedMargin(strategyPrice))} %`,
                      },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-100 shadow-sm"
                      >
                        <dt className="text-sm font-medium text-gray-500">
                          {label}
                        </dt>
                        <dd className="text-sm font-semibold text-gray-900 tabular-nums ml-4">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  {/* PriceInput rows */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                    <div className="p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Preço sem IVA:
                      </label>
                      <PriceInput
                        price={{
                          value: data.getPricePVPWithoutVat(strategyPrice),
                          currency: "EUR",
                        }}
                        allowChangeCurrency={false}
                        onChange={(updateValue) => {
                          const price_without_vat =
                            updateValue.value ??
                            data.getPricePVPWithoutVat(strategyPrice) ??
                            0;
                          const price_with_vat =
                            price_without_vat * (data?.vat_rate ?? vat);
                          setData({
                            ...data,
                            configured_price_pvp_without_vat:
                              toDecimal(price_without_vat),
                            configured_price_pvp_with_vat:
                              toDecimal(price_with_vat),
                          });
                        }}
                        readOnly={true}
                        inputClassName="w-full text-base font-semibold text-gray-900 border-none focus:outline-none bg-transparent"
                      />
                    </div>
                    <div className="p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Preço com IVA ({toPercentage(data?.vat_rate ?? vat)} %):
                      </label>
                      <PriceInput
                        price={{
                          value: data.getPricePVPWithVat(strategyPrice),
                          currency: "EUR",
                        }}
                        allowChangeCurrency={false}
                        onChange={(updateValue) => {
                          const price_with_vat =
                            updateValue.value ??
                            data.getPricePVPWithVat(strategyPrice) ??
                            0;
                          const price_without_vat =
                            price_with_vat / (data?.vat_rate ?? vat);
                          setData({
                            ...data,
                            configured_price_pvp_without_vat:
                              toDecimal(price_without_vat),
                            configured_price_pvp_with_vat:
                              toDecimal(price_with_vat),
                          });
                        }}
                        readOnly={true}
                        inputClassName="w-full text-base font-semibold text-gray-900 border-none focus:outline-none bg-transparent"
                      />
                    </div>
                  </div>

                  {/* H3 fix — editable fields in their own clean grid (no flex+grid conflict) */}
                  <div className="border-t border-gray-200 pt-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                      Campos Editáveis
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label
                          htmlFor="available_units"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Unidades Disponíveis
                        </label>
                        <input
                          id="available_units"
                          name="available_units"
                          type="number"
                          min={0}
                          className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={data.getAvailableUnits(strategyPrice)}
                          onChange={(e) =>
                            setData({
                              ...data,
                              available_units: parseInt(
                                e.target.value ?? "0",
                                10,
                              ),
                            })
                          }
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="category_slug"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Categoria
                        </label>
                        <select
                          id="category_slug"
                          defaultValue={data.category_slug}
                          onChange={(e) =>
                            setData({ ...data, category_slug: e.target.value })
                          }
                          className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          <option value="" disabled>
                            Selecione uma categoria
                          </option>
                          {categories.map((category) => (
                            <option key={category.slug} value={category.slug}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="status"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Estado
                        </label>
                        <select
                          id="status"
                          defaultValue={data.status}
                          onChange={(e) =>
                            setData({
                              ...data,
                              status: e.target.value as
                                | "DRAFT"
                                | "PENDING"
                                | "PUBLISHED"
                                | "HIDDEN"
                                | "ARCHIVED",
                            })
                          }
                          className="block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          {statusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* H4 fix — Tags: col-span-full instead of col-span-4 in a grid-cols-2 */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {availableTags.map((tag) => {
                        const isSelected = (data.tags ?? []).includes(
                          tag.value,
                        );
                        return (
                          <button
                            key={tag.value}
                            type="button"
                            onClick={() => {
                              const currentTags = data.tags ?? [];
                              const newTags = isSelected
                                ? currentTags.filter((t) => t !== tag.value)
                                : [...currentTags, tag.value];
                              setData({ ...data, tags: newTags });
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all cursor-pointer ${
                              isSelected
                                ? tag.color +
                                  " ring-2 ring-offset-1 ring-blue-400"
                                : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100"
                            }`}
                          >
                            {tag.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Changes Button */}
          <div className="flex justify-end mt-8">
            <button
              type="submit"
              className="px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-xl shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500 focus:ring-opacity-75 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!!loading?.message} // Disable button during loading
            >
              {!!loading?.message ? "A guardar..." : "Guardar Alterações"}
            </button>
          </div>
        </div>

        <div className="bg-white shadow-lg rounded-xl p-8 mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Componentes do Computador
            </h2>
            <button
              type="button"
              className="px-4 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75 transition duration-200 text-sm disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
              onClick={() => {
                if (!showAddComponentSection && !editedComponentData) {
                  setShowAddComponentSection(true);
                }
              }}
              disabled={
                !!loading?.message ||
                showAddComponentSection ||
                !!editedComponentData
              } // Disable if loading or already showing add section
            >
              + Adicionar Componente
            </button>
          </div>
          {showAddComponentSection && (
            <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-xl shadow-sm">
              <h4 className="text-sm font-semibold text-blue-800 mb-4">
                Adicionar novo componente
              </h4>
              <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                <select
                  className="block px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={newComponentType}
                  onChange={(e) => setNewComponentType(e.target.value)}
                >
                  <option value="">Selecione o tipo</option>
                  {Object.entries(OrderComponets).map(([_, type]) => {
                    if (
                      // @ts-ignore
                      data.getComponentTypesWithProducts().includes(type[0])
                    ) {
                      return null;
                    }
                    return (
                      <option key={type[0]} value={type[0]}>
                        {type[2]}
                      </option>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onClick={() => handleAddComponent(newComponentType)}
                  disabled={!newComponentType}
                >
                  Adicionar
                </button>
                <button
                  type="button"
                  className="px-4 py-2 bg-white text-gray-600 border border-gray-300 text-sm font-medium rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                  onClick={() => setShowAddComponentSection(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {Object.entries(OrderComponets).map(([, item]) => {
            const type = item[0];
            // @ts-ignore
            const componentsArray = data.getComponentListByType(type);

            const isCollapsed = collapsedSections[type];
            const baseComponent = componentsArray.find((c) => c.base_component); // Find the base component

            return (
              componentsArray.length > 0 && ( // Only render if there are components in this category
                <div
                  id={`section-` + type}
                  key={type}
                  className="bg-gray-50 rounded-lg shadow-sm p-2 mb-3"
                >
                  <div className="flex items-center justify-between mb-2 ">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={() => toggleCollapse(type)}
                    >
                      <p className="text-xl font-semibold text-gray-800 flex items-center">
                        {icons[type]}
                        <span className="ml-2 capitalize">
                          {getOrderComponentName(type)}
                        </span>{" "}
                      </p>
                      <button
                        type="button"
                        className="ml-4 p-1 rounded-full text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {isCollapsed ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronUp size={20} />
                        )}
                      </button>
                    </div>
                    {isCollapsed ? (
                      <div className="px-4 py-2">
                        {baseComponent ? (
                          <>
                            <p className="font-semibold">
                              {baseComponent.product_name} - Referência:{" "}
                              {baseComponent.product_reference}
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Nenhum componente base definido para este tipo.
                          </p>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75 transition duration-200 text-sm disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
                        onClick={() => {
                          if (
                            !showAddComponentSection &&
                            !editedComponentData
                          ) {
                            handleAddComponent(type);
                          }
                        }}
                        disabled={
                          !!loading?.message ||
                          showAddComponentSection ||
                          !!editedComponentData
                        } // Disable if loading or already showing add section
                      >
                        Adicionar Opção
                      </button>
                    )}
                  </div>

                  {!isCollapsed && (
                    // Display full table when expanded
                    <div className="overflow-x-auto border-t border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Base
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Referência
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Nome
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Descrição
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Marca
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Quantidade
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Preço Custo
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Preço REV4
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Preço Loja
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Preço Especial
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Stock
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Ações
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {componentsArray.map(
                            (x: ManagementCompoundComponent, i: number) => {
                              const c = NewManagementCompoundComponent(x);
                              const isEditing = editingComponentId === c.id;
                              const className =
                                c.checkPriceDifference() > 0
                                  ? "bg-green-100 text-green-800"
                                  : c.checkPriceDifference() < 0
                                    ? "bg-red-100 text-red-800"
                                    : c.getOverridePrice("configured")
                                      ? "bg-yellow-50 text-yellow-800"
                                      : "";
                              return (
                                <tr
                                  key={c.id || i}
                                  className={`hover:bg-gray-50 ${className} ${isEditing ? "bg-gray-50" : ""}`}
                                >
                                  <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {isEditing ? (
                                      <input
                                        type="checkbox"
                                        className="form-checkbox h-4 w-4 text-green-600"
                                        checked={
                                          !!editedComponentData?.base_component
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "base_component",
                                          )
                                        }
                                      />
                                    ) : c.base_component ? (
                                      <span className="text-green-600 font-bold">
                                        SIM
                                      </span>
                                    ) : (
                                      <span className="text-red-600">NÃO</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <ProductSearchInput
                                        allProducts={true}
                                        value={
                                          editedComponentData?.product_reference ??
                                          ""
                                        }
                                        onChange={(value) => {
                                          if (editedComponentData) {
                                            setEditedComponentData(
                                              NewManagementCompoundComponent({
                                                ...editedComponentData,
                                                product_reference: value,
                                              }),
                                            );
                                          }
                                        }}
                                        onSelect={(product) => {
                                          if (editedComponentData) {
                                            setEditedComponentData(
                                              NewManagementCompoundComponent({
                                                ...editedComponentData,
                                                product_reference: product.id,
                                                product_name: product.name,
                                                product_description:
                                                  product.short_description,
                                                product_brand: product.brand,
                                                configured_cost_price:
                                                  product.cost_price,
                                                erp_cost_price:
                                                  product.cost_price,
                                                erp_compound_reseller_price:
                                                  product.rev4_price,
                                                configured_compound_reseller_price:
                                                  product.rev4_price,
                                                configured_reseller_price:
                                                  product.rev3_price,
                                                erp_reseller_price:
                                                  product.rev3_price,
                                                quantity: 1,
                                                configured_available_quantity:
                                                  product.stock,
                                                erp_available_quantity:
                                                  product.stock,
                                              }),
                                            );
                                          }
                                        }}
                                        placeholder="Pesquisar por referência..."
                                      />
                                    ) : (
                                      c.product_reference
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.product_name ??
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "product_name",
                                          )
                                        }
                                      />
                                    ) : (
                                      c.product_name
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.product_description ??
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "product_description",
                                          )
                                        }
                                      />
                                    ) : (
                                      c.product_description
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.product_brand ??
                                          ""
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "product_brand",
                                          )
                                        }
                                      />
                                    ) : (
                                      (c.product_brand ?? "N/A")
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.quantity ?? 0
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "quantity",
                                          )
                                        }
                                      />
                                    ) : (
                                      c.quantity
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={editedComponentData?.getCostPrice(
                                          strategyPrice,
                                        )}
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "configured_cost_price",
                                          )
                                        }
                                      />
                                    ) : (
                                      toDecimal(c.getCostPrice(strategyPrice)) +
                                      " EUR"
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={editedComponentData?.getResellerPrice(
                                          strategyPrice,
                                        )}
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "configured_reseller_price",
                                          )
                                        }
                                      />
                                    ) : (
                                      toDecimal(
                                        c.getResellerPrice(strategyPrice),
                                      ) + " EUR"
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.getRev4Price(
                                            strategyPrice,
                                          ) ?? 0
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "configured_compound_reseller_price",
                                          )
                                        }
                                      />
                                    ) : (c.getRev4Price(strategyPrice) ??
                                      0 == 0) ? (
                                      toDecimal(
                                        c.getRev4Price(strategyPrice) ?? 0,
                                      ) + " EUR"
                                    ) : (
                                      "--"
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        step="0.01"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.getOverridePrice(
                                            strategyPrice,
                                          ) ?? 0
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "configured_override_price",
                                          )
                                        }
                                      />
                                    ) : (c.getOverridePrice(strategyPrice) ??
                                      0 == 0) ? (
                                      toDecimal(
                                        c.getOverridePrice(strategyPrice) ?? 0,
                                      ) + " EUR"
                                    ) : (
                                      "--"
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {isEditing ? (
                                      <input
                                        type="number"
                                        className="w-full border rounded px-2 py-1 text-gray-900"
                                        value={
                                          editedComponentData?.getAvailableQuantity(
                                            strategyPrice,
                                          ) ?? 0
                                        }
                                        onChange={(e) =>
                                          handleComponentInputChange(
                                            e,
                                            "configured_available_quantity",
                                          )
                                        }
                                      />
                                    ) : (
                                      <div className="flex items-center gap-1.5">
                                        <span>
                                          {c.getAvailableQuantity(
                                            strategyPrice,
                                          )}
                                        </span>
                                        {c.erp_synced_at ? (
                                          <span
                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-700"
                                            title="Stock sincronizado do SAG/ERP"
                                          >
                                            SAG
                                          </span>
                                        ) : (
                                          <span
                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700"
                                            title="Stock configurado manualmente (sem SAG)"
                                          >
                                            Configurado
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    {isEditing ? (
                                      <div className="flex items-center justify-end gap-2">
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                          onClick={handleSaveComponent}
                                          disabled={isLoadingActive}
                                        >
                                          Guardar
                                        </button>
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-600 border border-gray-300 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                          onClick={handleCancelEdit}
                                          disabled={isLoadingActive}
                                        >
                                          Cancelar
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-end gap-2">
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-indigo-600 border border-indigo-200 bg-indigo-50 text-xs font-medium rounded-lg hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                          onClick={() => handleEditComponent(c)}
                                          disabled={isLoadingActive}
                                          aria-label={`Editar ${c.product_name}`}
                                        >
                                          <Pencil
                                            className="w-3.5 h-3.5"
                                            aria-hidden="true"
                                          />
                                          Editar
                                        </button>
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-red-600 border border-red-200 bg-red-50 text-xs font-medium rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                          onClick={() => {
                                            if (
                                              window.confirm(
                                                `Tem a certeza que quer remover "${c.product_name}"? Esta acção não pode ser desfeita.`,
                                              )
                                            ) {
                                              handleDeleteComponent(c);
                                            }
                                          }}
                                          disabled={isLoadingActive}
                                          aria-label={`Remover ${c.product_name}`}
                                        >
                                          <Trash2
                                            className="w-3.5 h-3.5"
                                            aria-hidden="true"
                                          />
                                          Remover
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            },
                          )}
                          {componentsArray.length === 0 && (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center"
                              >
                                Nenhum componente encontrado para esta
                                categoria.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            );
          })}
          {/* Message if no components at all */}
          {Array.from(data.getComponentsMap()).every(
            ([key, components]) => components.length === 0,
          ) && (
            <div className="text-center text-gray-600 p-4">
              Nenhum componente configurado para este computador.
            </div>
          )}
        </div>
      </form>

      {isAdmin && id && <ActiveCampaignsSection key={id} compoundId={id} />}

      {isAdmin && id && (
        <div
          role="tabpanel"
          id="tabpanel-compound-history"
          aria-labelledby="tab-compound-history"
          hidden={activeTab !== "history"}
        >
          {activeTab === "history" && (
            <CompoundPriceHistorySection key={id} compoundId={id} />
          )}
        </div>
      )}
    </div>
  );
};

export default CompoundManageEditPage;
